import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, ArrowRight, ChevronDown, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { tasksService } from './tasksService';
import TaskPlannerModal from './TaskPlannerModal';
import './TasksRecentCard.css';

const SHOW_COUNT = 4;
const COLLAPSE_KEY = 'revstar_tasks_card_collapsed';

function endOfToday() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function plusDays(days) {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  d.setDate(d.getDate() + days);
  return d;
}

function timeLabel(task) {
  if (task.allDay) return 'All day';
  const d = new Date(task.dueAt);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  if (same) return d.toLocaleTimeString('en-NG', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-NG', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

const TasksRecentCard = () => {
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const refresh = useCallback(async () => {
    try {
      // Today + next 7 days, excluding DONE — the "what's coming up" view.
      // TODO + IN_PROGRESS both show; status badges distinguish them.
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const data = await tasksService.list({
        from: start.toISOString(),
        to: plusDays(7).toISOString()
      });
      const upcoming = data.filter((t) => t.status !== 'DONE');
      setTasks(upcoming);
    } catch (err) {
      console.warn('[TasksRecentCard] load failed', err?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  const statusLabel = (s) => (s === 'TODO' ? 'To do' : s === 'DONE' ? 'Done' : 'In progress');

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (!open) refresh(); }, [open, refresh]);

  const handleToggle = async (e, task) => {
    e.stopPropagation();
    try {
      await tasksService.toggle(task.id);
      // Optimistic remove (it's now DONE and shouldn't show in "upcoming")
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (err) {
      // Re-fetch on failure to resync
      refresh();
    }
  };

  const visible = tasks.slice(0, SHOW_COUNT);

  return (
    <>
      <section className={`ov-card rstp-card ${collapsed ? 'is-collapsed' : ''}`} aria-label="Tasks">
        <header className="rstp-card__head">
          <button
            type="button"
            className="rstp-card__toggle"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand tasks' : 'Collapse tasks'}
          >
            <span className="rstp-card__icon" aria-hidden="true">
              <CalendarClock size={16} strokeWidth={2} />
            </span>
            <span className="rstp-card__heads">
              <span className="rstp-card__title">Tasks</span>
              <span className="rstp-card__sub">
                {!loaded
                  ? 'Loading…'
                  : tasks.length === 0
                    ? 'Nothing on the docket'
                    : `Upcoming · ${tasks.length}`}
              </span>
            </span>
            <span className="rstp-card__chevron" aria-hidden="true">
              <ChevronDown size={16} strokeWidth={2} />
            </span>
          </button>
          <button
            type="button"
            className="rstp-card__open"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            aria-label="Open tasks"
          >
            Open
          </button>
        </header>

        <div className="rstp-card__collapse">
          <div className="rstp-card__collapse-inner">
            {loaded && visible.length > 0 ? (
              <ul className="rstp-card__list" role="list">
                {visible.map((t) => (
                  <li key={t.id}>
                    <div className="rstp-card__entry" onClick={() => setOpen(true)} role="button" tabIndex={0}
                         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}>
                      <button
                        type="button"
                        className="rstp-card__check"
                        onClick={(e) => handleToggle(e, t)}
                        aria-label="Mark as done"
                      >
                        <Check size={11} aria-hidden="true" />
                      </button>
                      <span className="rstp-card__entry-body">
                        <span className="rstp-card__entry-row">
                          <span className="rstp-card__entry-title">{t.title}</span>
                          <span className={`rstp-card__badge rstp-card__badge--${t.status.toLowerCase()}`}>
                            {statusLabel(t.status)}
                          </span>
                        </span>
                        <span className="rstp-card__entry-time">{timeLabel(t)}</span>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : loaded ? (
              <button
                type="button"
                className="rstp-card__empty"
                onClick={() => setOpen(true)}
              >
                Plan something — it'll show up here.
              </button>
            ) : null}

            {tasks.length > SHOW_COUNT && (
              <Link to="/utilities/tasks" className="rstp-card__more">
                See all <ArrowRight size={12} aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <TaskPlannerModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default TasksRecentCard;
